/**
 * GuestModeProvider — declares whether the current route is being rendered for
 * an anonymous "guest" visitor.
 *
 * Guest mode is set only when:
 *   - the route is marked `authRequired: true, guestViewAllowed: true`, AND
 *   - the visitor has no access token.
 *
 * Authenticated visitors and private (`guestViewAllowed: false`) routes never
 * see `isGuest: true`. The guard in `main.tsx` is the only place that sets it.
 *
 * Components downstream should consult `useGuestMode()` instead of inferring
 * from `useAuth()` alone, because the user may be unauthenticated on a route
 * that nonetheless allows guest rendering.
 */
import { createContext, useContext, useMemo, type ReactNode } from "react";

export interface GuestModeContextValue {
  /** True when an `authRequired` route is being rendered without a token. */
  isGuest: boolean;
}

const GuestModeContext = createContext<GuestModeContextValue>({ isGuest: false });

export interface GuestModeProviderProps {
  isGuest: boolean;
  children: ReactNode;
}

export function GuestModeProvider({ isGuest, children }: GuestModeProviderProps) {
  const value = useMemo<GuestModeContextValue>(() => ({ isGuest }), [isGuest]);
  return (
    <GuestModeContext.Provider value={value}>
      {children}
    </GuestModeContext.Provider>
  );
}

export function useGuestMode(): GuestModeContextValue {
  return useContext(GuestModeContext);
}
